import { useEffect, useState, useRef, useCallback } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, MapPin, Airplane, Trophy, Sparkle, SignOut, Plus, GlobeHemisphereWest, Globe, Star, Compass, ChatCircle, ImageSquare, FilmSlate, Suitcase, Gear, BookmarkSimple, ShieldCheck, Check } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { postsApi, stampsApi, authApi } from "@/lib/apiClient";
import PlannerModal from "@/components/PlannerModal";
import WorldMap from "@/components/WorldMap";
import BucketList from "@/components/BucketList";
import ReviewsPanel from "@/components/ReviewsPanel";
import Messages from "@/components/Messages";
import CommentsDrawer from "@/components/CommentsDrawer";
import ImageUploader from "@/components/ImageUploader";
import CountryDrawer from "@/components/CountryDrawer";
import Stories from "@/components/Stories";
import Reels from "@/components/Reels";

import Trips from "@/components/Trips";
import PostCard from "@/components/PostCard";
import CreatePost from "@/components/CreatePost";
import PassportAppHeader from "@/components/PassportAppHeader";
import { SupportBubble } from "@/components/SupportChat";
import SavedCollections from "@/components/SavedCollections";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const TABS = [
    { key: "atlas", label: "Atlas", icon: GlobeHemisphereWest },
    { key: "feed", label: "Feed", icon: ImageSquare },
    { key: "reels", label: "Reels", icon: FilmSlate },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "trips", label: "Trips", icon: Suitcase },
    { key: "saved", label: "Saved", icon: BookmarkSimple },
    { key: "bucket", label: "Bucket", icon: Compass },
    { key: "messages", label: "Messages", icon: ChatCircle },
];

export default function Dashboard() {
    const { user, loading, setUser } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "atlas";
    const [tab, setTab] = useState(initialTab);
    const [feedMode, setFeedMode] = useState("all"); // 'all' | 'following'
    const [atlasRegion, setAtlasRegion] = useState("World");
    const [atlasMode, setAtlasMode] = useState("heatmap");
    const [posts, setPosts] = useState([]);
    const [feedCursor, setFeedCursor] = useState(null);
    const [hasMoreFeed, setHasMoreFeed] = useState(true);
    const [loadingFeed, setLoadingFeed] = useState(false);
    const [stamps, setStamps] = useState([]);
    const [planner, setPlanner] = useState(false);
    const [addStamp, setAddStamp] = useState(false);
    const [addPost, setAddPost] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null); // opens drawer
    const [countryFilter, setCountryFilter] = useState(null); // for stamps grid filter
    const [commentPost, setCommentPost] = useState(null);
    const fetchIdRef = useRef(0);

    const loadStamps = useCallback(() => stampsApi.list().then(setStamps).catch(() => {}), []);
    const loadFeed = async (cursor = null, isNewMode = false) => {
        if (loadingFeed && !isNewMode) return;
        const fetchId = ++fetchIdRef.current;
        setLoadingFeed(true);
        try {
            const params = { limit: 30 };
            if (cursor) params.cursor = cursor;
            if (feedMode === "following") params.mode = "following";
            
            const r = await postsApi.list(params);
            if (fetchId !== fetchIdRef.current) return;
            
            if (cursor && !isNewMode) {
                setPosts(prev => [...prev, ...(r.items || [])]);
            } else {
                setPosts(r.items || []);
            }
            
            setFeedCursor(r.next_cursor);
            setHasMoreFeed(!!r.next_cursor);
        } catch (e) {
            if (fetchId !== fetchIdRef.current) return;
            if (!cursor) setPosts([]);
        } finally {
            if (fetchId === fetchIdRef.current) setLoadingFeed(false);
        }
    };

    const observer = useRef(null);
    const lastPostElementRef = useCallback(node => {
        if (loadingFeed) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMoreFeed) {
                loadFeed(feedCursor);
            }
        });
        if (node) observer.current.observe(node);
    }, [loadingFeed, hasMoreFeed, feedCursor, loadFeed]);

    useEffect(() => {
        if (!user) return;
        setFeedCursor(null);
        setHasMoreFeed(true);
        loadFeed(null, true);
        loadStamps();
        // eslint-disable-next-line
    }, [user, feedMode]);

    useEffect(() => {
        const onFocus = () => {
            loadStamps();
            authApi.me().then(setUser).catch(() => {});
        };
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [setUser, loadStamps]);

    // Listen for atlas:refresh dispatched by TravelDateDetail after marking a date complete
    useEffect(() => {
        const onAtlasRefresh = () => {
            loadStamps();
            authApi.me().then(setUser).catch(() => {});
        };
        window.addEventListener("atlas:refresh", onAtlasRefresh);
        return () => window.removeEventListener("atlas:refresh", onAtlasRefresh);
    }, [setUser, loadStamps]);

    useEffect(() => {
        const nextTab = searchParams.get("tab");
        const nextCountry = searchParams.get("country");
        if (nextTab && TABS.some((t) => t.key === nextTab)) {
            setTab(nextTab);
        }
        if (nextTab === "atlas" && nextCountry) {
            setCountryFilter(nextCountry);
            setSelectedCountry(nextCountry);
        }
    }, [searchParams]);

    if (loading) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    if (!user) return <Navigate to="/auth" />;

    return (
        <div className="min-h-screen grain" data-testid="dashboard">
            <PassportAppHeader homeHref="/" subtitle="Dashboard" />

            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-10">
                    <div className="overline mb-3">{t("dashboard.journey")}</div>
                    <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tighter leading-[0.95]">
                        {t("dashboard.hello")} <span style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{user?.name || user?.handle}</span>.
                    </h1>
                    <p className="text-white/60 text-lg mt-4 max-w-lg">
                        {t("dashboard.atlasCount", { count: user.countries_visited })} {t("dashboard.levelTier", { level: user.level, tier: user.tier })}
                    </p>
                </motion.div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    {[
                        { icon: Flag, label: t("dashboard.stats.countries"), value: user?.countries_visited ?? 0 },
                        { icon: MapPin, label: t("dashboard.stats.places"), value: user?.places ?? 0 },
                        { icon: Airplane, label: t("dashboard.stats.miles"), value: (user?.miles || 0).toLocaleString() },
                        { 
                            icon: Trophy, 
                            label: t("dashboard.stats.level"), 
                            value: user?.level || 1,
                            xpBadge: user?.xp !== undefined ? `${(user.xp || 0).toLocaleString()} XP` : null,
                            progress: user?.level_progress_pct,
                            xpToNext: user?.xp_to_next_level
                        },
                    ].map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                                className="rounded-2xl p-6 flex flex-col justify-between" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
                                data-testid={`dash-stat-${s.label.toLowerCase()}`}
                            >
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Icon size={20} color="#D4AF37" />
                                        {s.xpBadge && (
                                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.25)" }}>
                                                {s.xpBadge}
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-serif text-4xl text-white mt-4 leading-none">{s.value}</div>
                                </div>
                                <div className="mt-3">
                                    {s.progress !== undefined && s.progress !== null ? (
                                        <div>
                                            <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(4, s.progress)}%`, background: "linear-gradient(90deg, #D4AF37, #F4A28F)" }} />
                                            </div>
                                            <div className="flex justify-between items-center text-[10px] tracking-[0.18em] uppercase text-white/50 mt-1.5">
                                                <span>{s.label}</span>
                                                {s.xpToNext ? <span className="normal-case tracking-normal text-[10px] text-white/40">{s.xpToNext} XP to Lvl {(user?.level || 1) + 1}</span> : null}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">{s.label}</div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Tabs */}
                <div className="flex gap-2 flex-wrap mb-8 p-1 rounded-full w-max" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="dash-tabs">
                    {TABS.map((tItem) => {
                        const Icon = tItem.icon;
                        const active = tab === tItem.key;
                        return (
                            <button key={tItem.key} onClick={() => {
                                if (tItem.key === "messages") { navigate("/messages"); return; }
                                setTab(tItem.key); setSearchParams({ tab: tItem.key });
                            }}
                                className="px-4 py-2.5 rounded-full text-sm flex items-center gap-2 transition-colors duration-300"
                                style={{ background: active ? "var(--gold)" : "transparent", color: active ? "var(--navy)" : "white" }}
                                data-testid={`dash-tab-${tItem.key}`}
                            >
                                <Icon size={14} weight={active ? "fill" : "regular"} />
                                {t(`dashboard.tabs.${tItem.key}`)}
                            </button>
                        );
                    })}
                </div>

                <AnimatePresence mode="popLayout">
                    <motion.div key={tab}
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4 }}
                    >
                        {tab === "atlas" && (
                            <div>
                                {/* Redesigned Atlas header — matches user's Your Atlas reference */}
                                <div className="flex items-start justify-between mb-6 gap-3">
                                    <div>
                                        <div className="text-[10px] tracking-[0.35em] uppercase mb-1" style={{ color: "var(--gold)" }}>{t("dashboard.atlas.worldMap")}</div>
                                        <h2 className="font-serif text-4xl md:text-5xl text-white leading-[0.95] tracking-tighter">{t("dashboard.atlas.yourAtlas")}</h2>
                                    </div>
                                    <button
                                        onClick={() => setAddStamp(true)}
                                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-transform active:scale-95"
                                        style={{ background: "#E4C46D", color: "#1a0e0a" }}
                                        data-testid="atlas-add-stamp"
                                    >
                                        <Plus size={14} weight="bold" /> {t("dashboard.atlas.addStamp")}
                                    </button>
                                </div>

                                {/* World exploration hero — mountain image + big % + stat pills */}
                                <div
                                    className="rounded-3xl overflow-hidden relative aspect-[16/11] md:aspect-[16/7] mb-6"
                                    style={{ border: "1px solid rgba(255,255,255,0.06)" }}
                                    data-testid="atlas-hero"
                                >
                                    <img
                                        src="https://images.pexels.com/photos/1252500/pexels-photo-1252500.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
                                        alt="World map hero — mountain lake"
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.3) 0%, rgba(5,10,17,0.85) 100%)" }} />
                                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                                        <div className="font-serif text-6xl md:text-8xl leading-none" style={{ color: "var(--gold)" }} data-testid="atlas-percent">
                                            {(user.world_explored ?? 0).toFixed(1)}%
                                        </div>
                                        <div className="text-[11px] tracking-[0.3em] uppercase text-white/85 mt-2 mb-5">{t("dashboard.atlas.ofWorldExplored")}</div>
                                        <div className="flex flex-wrap gap-2">
                                            <StatPill icon={Flag} label={`${user?.countries_visited || 0} ${t("dashboard.stats.countries").toLowerCase()}`} tone="coral" />
                                            <StatPill icon={MapPin} label={`${user?.places || 0} ${t("dashboard.stats.places").toLowerCase()}`} tone="teal" />
                                            <StatPill icon={Airplane} label={`Lvl ${user?.level || 1}`} tone="gold" />
                                            {user?.completed_trips > 0 && (
                                                <StatPill icon={Suitcase} label={`${user.completed_trips} ${user.completed_trips === 1 ? 'trip' : 'trips'}`} tone="teal" />
                                            )}
                                            {user?.completed_dates > 0 && (
                                                <StatPill icon={Sparkle} label={`${user.completed_dates} ${user.completed_dates === 1 ? 'date' : 'dates'}`} tone="coral" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Region filter */}
                                <div className="mb-4 -mx-6 md:mx-0 px-6 md:px-0 overflow-x-auto no-scrollbar">
                                    <div className="flex gap-2 pb-1">
                                        {["World", "Europe", "Asia", "Africa", "N. America", "S. America", "Oceania"].map((r, i) => {
                                            const mapKeys = { "World": "world", "Europe": "europe", "Asia": "asia", "Africa": "africa", "N. America": "namerica", "S. America": "samerica", "Oceania": "oceania" };
                                            return (
                                                <button
                                                    key={r}
                                                    onClick={() => setAtlasRegion(r)}
                                                    className="px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
                                                    style={{
                                                        background: atlasRegion === r ? "#F4A28F" : "rgba(255,255,255,0.04)",
                                                        color: atlasRegion === r ? "#1a0e0a" : "#9FD5D0",
                                                        border: `1px solid ${atlasRegion === r ? "#F4A28F" : "rgba(159,213,208,0.2)"}`,
                                                    }}
                                                    data-testid={`atlas-region-${r.replace(/\W+/g, "").toLowerCase()}`}
                                                >
                                                    {t(`dashboard.atlas.regions.${mapKeys[r]}`)}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                {/* Map mode selector */}
                                <div className="mb-8 flex flex-wrap gap-2">
                                    {[
                                        { k: "heatmap", l: t("dashboard.atlas.modes.heatmap"), icon: "🔥", peach: true },
                                        { k: "routes", l: t("dashboard.atlas.modes.routes"), icon: "🗺", teal: true },
                                        { k: "live", l: t("dashboard.atlas.modes.live"), icon: "🟢" },
                                        { k: "scratch", l: t("dashboard.atlas.modes.scratch"), icon: "⭐", gold: true },
                                    ].map((m) => (
                                        <button
                                            key={m.k}
                                            onClick={() => setAtlasMode(m.k)}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors"
                                            style={{
                                                background: atlasMode === m.k
                                                    ? (m.peach ? "#F4A28F" : m.teal ? "rgba(159,213,208,0.12)" : m.gold ? "rgba(212,175,55,0.14)" : "rgba(255,255,255,0.06)")
                                                    : "rgba(255,255,255,0.04)",
                                                color: atlasMode === m.k
                                                    ? (m.peach ? "#1a0e0a" : m.teal ? "#9FD5D0" : m.gold ? "var(--gold)" : "white")
                                                    : "#9FD5D0",
                                                border: `1px solid ${atlasMode === m.k
                                                    ? (m.peach ? "#F4A28F" : m.teal ? "rgba(159,213,208,0.4)" : m.gold ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.15)")
                                                    : "rgba(159,213,208,0.15)"}`,
                                            }}
                                            data-testid={`atlas-mode-${m.k}`}
                                        >
                                            <span>{m.icon}</span> {m.l}
                                        </button>
                                    ))}
                                </div>

                                {/* Interactive world map */}
                                <div className="mb-10 rounded-3xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                    <WorldMap
                                        stamps={stamps}
                                        mode={atlasMode}
                                        region={atlasRegion}
                                        onCountryClick={(name, isVisited) => { setCountryFilter(name); setSelectedCountry(name); }}
                                    />
                                </div>

                                {/* Countries Visited grid — grouped by unique country */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-serif text-3xl text-white leading-none">{t("dashboard.atlas.countriesVisited")}</h3>
                                        {countryFilter && (
                                            <button onClick={() => setCountryFilter(null)} className="text-white/60 hover:text-white text-[11px] tracking-wider">{t("dashboard.atlas.clearFilter")}</button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {(() => {
                                            const ALIASES = {
                                                "usa": "United States of America",
                                                "us": "United States of America",
                                                "u.s.a.": "United States of America",
                                                "united states": "United States of America",
                                                "united states of america": "United States of America",
                                                "united state of america": "United States of America",
                                                "uk": "United Kingdom",
                                                "u.k.": "United Kingdom",
                                                "united kingdom": "United Kingdom",
                                                "great britain": "United Kingdom",
                                                "england": "United Kingdom",
                                                "scotland": "United Kingdom",
                                                "wales": "United Kingdom",
                                                "northern ireland": "United Kingdom",
                                                "uae": "United Arab Emirates",
                                                "u.a.e.": "United Arab Emirates",
                                            };
                                            const countryMap = new Map();
                                            stamps.forEach((s) => {
                                                if (!s.country) return;
                                                const raw = s.country.trim();
                                                const normalized = ALIASES[raw.toLowerCase()] || raw;
                                                const key = normalized.toLowerCase();
                                                if (!countryMap.has(key)) {
                                                    countryMap.set(key, { name: normalized, cities: new Set() });
                                                }
                                                if (s.city) countryMap.get(key).cities.add(s.city.trim());
                                            });
                                            const list = Array.from(countryMap.values()).filter(
                                                (c) => !countryFilter || c.name.toLowerCase() === countryFilter.toLowerCase()
                                            );

                                            return (
                                                <>
                                                    {list.map((c) => (
                                                        <button
                                                            key={c.name}
                                                            onClick={() => setSelectedCountry(c.name)}
                                                            className="text-left rounded-2xl p-4 relative transition-transform active:scale-[0.98]"
                                                            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
                                                            data-testid={`atlas-country-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                                        >
                                                            <div className="w-10 h-10 rounded-full grid place-items-center absolute top-3 right-3" style={{ background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.5)" }}>
                                                                <Check size={12} weight="bold" color="#D4AF37" />
                                                            </div>
                                                            <div className="font-serif text-xl text-white leading-tight mt-6" style={{ opacity: 0.8 }}>{c.name}</div>
                                                            <div className="text-[10px] text-white/50 uppercase tracking-[0.15em] mt-1">
                                                                {c.cities.size > 0 ? (c.cities.size === 1 ? t("dashboard.atlas.placesCount_one") : t("dashboard.atlas.placesCount_other", { count: c.cities.size })) : t("dashboard.atlas.placesCount_one")}
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {list.length === 0 && (
                                                        <div className="col-span-full text-center py-10 text-white/50 text-sm">
                                                            {t("dashboard.atlas.noStamps")}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )}

                        {tab === "feed" && (
                            <div>
                                <Stories />

                                {/* Redesigned header — mobile-first, matches user's Travel Feed reference */}
                                <div className="flex items-start justify-between mb-8 gap-3">
                                    <div>
                                        <div className="text-[10px] tracking-[0.35em] uppercase mb-1" style={{ color: "var(--gold)" }}>{t("feed.overline")}</div>
                                        <h2 className="font-serif text-4xl md:text-5xl text-white leading-[0.95] tracking-tighter">{t("feed.title")}</h2>
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                        <Link
                                            to="/discover"
                                            className="w-11 h-11 rounded-full grid place-items-center transition-transform active:scale-95"
                                            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
                                            title={t("nav.discover")}
                                            data-testid="feed-discover"
                                        >
                                            <Globe size={18} weight="fill" color="#D4AF37" />
                                        </Link>
                                        <button
                                            onClick={() => setAddPost(true)}
                                            className="w-11 h-11 rounded-full grid place-items-center transition-transform active:scale-95"
                                            style={{ background: "#F4A28F", color: "#1a0e0a" }}
                                            title={t("feed.addPost")}
                                            data-testid="feed-add"
                                        >
                                            <Plus size={18} weight="bold" />
                                        </button>
                                    </div>
                                </div>
                                <div className="mb-6 flex gap-1 p-1 rounded-full w-max" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="feed-mode">
                                    {[
                                        { k: "all", l: t("feed.explore") },
                                        { k: "following", l: t("feed.following") },
                                    ].map((m) => (
                                        <button key={m.k} onClick={() => setFeedMode(m.k)}
                                            className="px-4 py-1.5 rounded-full text-xs transition-colors"
                                            style={{ background: feedMode === m.k ? "var(--gold)" : "transparent", color: feedMode === m.k ? "var(--navy)" : "rgba(255,255,255,0.8)" }}
                                            data-testid={`feed-mode-${m.k}`}
                                        >{m.l}</button>
                                    ))}
                                </div>
                                {posts.length > 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {posts.map((p) => (
                                            <PostCard
                                                key={p.id}
                                                post={p}
                                                onOpenComments={setCommentPost}
                                                onChanged={() => loadFeed(null, true)}
                                                onDeleted={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
                                                onUpdate={(updates) => setPosts((prev) => prev.map((post) => post.id === p.id ? { ...post, ...updates } : post))}
                                            />
                                        ))}
                                    </div>
                                )}
                                {hasMoreFeed && posts.length > 0 && (
                                    <div ref={lastPostElementRef} className="text-center py-6 text-white/50 text-sm w-full">
                                        {loadingFeed ? t("dashboard.feed.loadingOlder") : t("dashboard.feed.scrollMore")}
                                    </div>
                                )}
                                {posts.length === 0 && !loadingFeed && feedMode === "following" && (
                                    <div className="text-center py-16 rounded-2xl w-full" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                                        <div className="text-white/60">{t("feed.noPostsFollowing")}</div>
                                    </div>
                                )}
                                {posts.length === 0 && !loadingFeed && feedMode === "all" && (
                                    <div className="text-center py-16 rounded-2xl w-full" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                                        <div className="font-serif text-2xl text-white mb-2">{t("dashboard.feed.journeyStarts")}</div>
                                        <div className="text-white/60 text-sm mb-5">{t("feed.noPostsExplore")}</div>
                                        <button onClick={() => setAddPost(true)} className="btn-gold text-sm !py-2.5 !px-5" data-testid="feed-empty-create">
                                            <Plus size={14} weight="bold" /> {t("feed.addPost")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {tab === "reels" && <Reels onReelCreated={() => loadFeed(null, true)} />}
                        {tab === "reviews" && <ReviewsPanel />}
                        {tab === "trips" && <Trips onOpenPlanner={() => setPlanner(true)} />}
                        {tab === "saved" && <SavedCollections />}
                        {tab === "bucket" && <BucketList />}
                        {tab === "messages" && <Messages />}
                    </motion.div>
                </AnimatePresence>
            </div>

            <PlannerModal open={planner} onClose={() => setPlanner(false)} onSaved={() => setPlanner(false)} />
            <AddStampModal open={addStamp} onClose={() => setAddStamp(false)} onSaved={() => { setAddStamp(false); loadStamps(); authApi.me().then(setUser); }} />
            <CreatePost open={addPost} onClose={() => setAddPost(false)} onCreated={() => { setAddPost(false); loadFeed(); }} />
            <CommentsDrawer 
                post={commentPost} 
                open={!!commentPost} 
                onClose={() => setCommentPost(null)} 
                onCountChange={(newCount) => setPosts((prev) => prev.map((p) => p.id === commentPost?.id ? { ...p, comments: newCount } : p))}
            />
            <CountryDrawer country={selectedCountry} open={!!selectedCountry} onClose={() => setSelectedCountry(null)} />
            <SupportBubble />
        </div>
    );
}

export const WORLD_COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
    "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
    "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
    "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
    "Oman",
    "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar",
    "Romania", "Russia", "Rwanda",
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States of America", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen",
    "Zambia", "Zimbabwe"
];

function AddStampModal({ open, onClose, onSaved }) {
    const { t } = useTranslation();
    const [country, setCountry] = useState("");
    const [city, setCity] = useState("");
    const [note, setNote] = useState("");
    const [image, setImage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [citySuggestions, setCitySuggestions] = useState([]);

    // Live City Autocomplete via OpenStreetMap Nominatim
    useEffect(() => {
        if (!city || city.length < 2 || !country) {
            setCitySuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&limit=5`);
                if (res.ok) {
                    const data = await res.json();
                    const names = Array.from(new Set(data.map((item) => item.display_name.split(",")[0].trim())));
                    setCitySuggestions(names);
                }
            } catch (err) {
                // ignore
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [city, country]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await stampsApi.create({ country, city, note, image_url: image });
            toast.success(t("dashboard.addStamp.success"));
            setCountry(""); setCity(""); setNote(""); setImage(null); setCitySuggestions([]);
            onSaved?.();
        } catch (err) { 
            const backendMsg = err.response?.data?.detail;
            toast.error(backendMsg || t("dashboard.addStamp.error")); 
        } finally { setBusy(false); }
    };
    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }} onClick={onClose}>
                    <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="rounded-3xl max-w-md w-full p-8" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }} data-testid="add-stamp-modal">
                        <div className="overline mb-2">{t("dashboard.addStamp.newStamp")}</div>
                        <h3 className="font-serif text-3xl text-white mb-6">{t("dashboard.addStamp.whereWereYou")}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">{t("dashboard.addStamp.country")}</label>
                                <select 
                                    required 
                                    value={country} 
                                    onChange={(e) => { setCountry(e.target.value); setCity(""); setCitySuggestions([]); }} 
                                    className="input cursor-pointer" 
                                    data-testid="stamp-country"
                                >
                                    <option value="" disabled>{t("dashboard.addStamp.selectCountry")}</option>
                                    {WORLD_COUNTRIES.map((c) => (
                                        <option key={c} value={c} className="bg-slate-900 text-white">{c}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="relative">
                                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">{t("dashboard.addStamp.cityPlace")}</label>
                                <input 
                                    required 
                                    disabled={!country}
                                    placeholder={country ? t("dashboard.addStamp.typeCity") : t("dashboard.addStamp.selectCountryFirst")} 
                                    value={city} 
                                    onChange={(e) => setCity(e.target.value)} 
                                    className="input" 
                                    data-testid="stamp-city" 
                                />
                                {citySuggestions.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 z-20 mt-1 rounded-xl p-1 shadow-2xl" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.3)" }}>
                                        {citySuggestions.map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                onClick={() => { setCity(s); setCitySuggestions([]); }}
                                                className="w-full text-left px-3 py-2 text-sm text-white/90 hover:bg-white/10 rounded-lg transition-colors"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <input placeholder={t("dashboard.addStamp.note")} value={note} onChange={(e) => setNote(e.target.value)} className="input" data-testid="stamp-note" />
                            <ImageUploader value={image} onChange={setImage} label={t("dashboard.addStamp.photo")} testId="stamp-image" height={140} />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">{t("common.cancel")}</button>
                            <button type="submit" disabled={busy} className="btn-gold flex-1 justify-center" data-testid="stamp-save">{busy ? "…" : t("dashboard.addStamp.submit")}</button>
                        </div>
                        <style>{`.input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; } .input::placeholder { color: rgba(255,255,255,0.35); } .input:focus { outline: none; border-color: var(--gold); }`}</style>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function AddPostModal() { return null; /* replaced by CreatePost */ }

function StatPill({ icon: Icon, label, tone = "gold" }) {
    const toneMap = {
        coral: { bg: "rgba(244,162,143,0.16)", border: "rgba(244,162,143,0.5)", fg: "#F4A28F" },
        teal: { bg: "rgba(159,213,208,0.14)", border: "rgba(159,213,208,0.45)", fg: "#9FD5D0" },
        gold: { bg: "rgba(212,175,55,0.16)", border: "rgba(212,175,55,0.5)", fg: "var(--gold)" },
    };
    const t = toneMap[tone] || toneMap.gold;
    return (
        <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
            style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.fg }}
        >
            <Icon size={13} weight="fill" />
            {label}
        </div>
    );
}
